/**
 * Single registry of every localStorage key the app reads or writes.
 *
 * Adding a new persisted preference? Add a key here first, then import
 * the constant — typos and string-drift become a TypeScript error
 * instead of a silent miss in production.
 *
 * The `emlx:` namespace is the contract; anything outside it is either
 * the legacy `mime-inspector.*` namespace (still in use, gradually
 * migrating) or a third-party key we don't own.
 *
 * Pattern keys (overview-panel:*, security-panel:*) are documented as
 * prefixes; per-panel suffixes are appended at call sites.
 */

export const STORAGE_KEYS = {
  // Application chrome
  theme: "emlx:theme",
  density: "emlx:density",
  locale: "emlx:locale",

  // Inspector preferences
  defaultTab: "emlx:default-tab",
  sourceCollapsed: "emlx:source-collapsed",
  htmlWrap: "emlx:html-wrap",
  rawWrap: "emlx:raw-wrap",
  previewAppearance: "emlx:preview-appearance",
  previewRemote: "emlx:preview-remote",
  /**
   * Catalog C7.2 — last viewport pick for the Preview tab (mobile / tablet /
   * full). Restored on next parse so the user's device-frame preference
   * survives reloads.
   */
  previewViewport: "emlx:preview-viewport",
  /**
   * Workbench Vorschau format toggle (html / text / diff). The post-iter400
   * surface defaulted to HTML/Text based on whichever body existed;
   * persisting the explicit pick lets the user land in Diff or Text by
   * default if that's where they live.
   */
  previewMode: "emlx:preview-mode",
  /**
   * Workbench Vorschau zoom factor. Stored as a string: either "fit" or a
   * percentage like "100" / "125" / "150". Out-of-range values fall back to
   * "fit".
   */
  previewZoom: "emlx:preview-zoom",
  /** Timeline density toggle (compact / expanded). */
  timelineDensity: "emlx:timeline-density",

  // Persisted user data
  /** Waiting-SW script URL whose update chip was dismissed (sessionStorage). */
  updateChipDismissed: "emlx:update-chip-dismissed",
  /** Command palette per-session Recent bucket (sessionStorage). */
  cmdkRecent: "emlx:cmdk-recent",
  /** Last uncaught error report (crash recovery surface). */
  lastError: "emlx:last-error",
  /** The preferences blob (enveloped {v,data}). */
  settings: "emlx:settings",
  bookmarks: "emlx:bookmarks",
  recents: "emlx:recents",
  savedViews: "emlx:saved-views",
  headersPresets: "emlx:headers-presets",
  linksPresets: "emlx:links-presets",
  headersScope: "emlx:headers-scope",
  // iter186 cross-link: Overview's "View content headers" CTA writes the
  // pre-selected category here; HeadersPanel reads it on mount so the
  // user lands on the matching strip-tab. Catalog C3.1.
  headersActiveCategory: "emlx:headers-active-category",

  // Welcome / onboarding
  introSeen: "emlx:intro-seen",
  // First-run "language auto-selected — switch?" hint. Set to "1" once the
  // user dismisses the hint (or opens Settings → Language from it) so it never
  // re-appears. Only ever shown when the locale preference is still "system"
  // AND the auto-detected code is non-English — i.e. the served document said
  // English but the visitor was routed to another language.
  localeAutoHintDismissed: "emlx:locale-auto-hint-dismissed",

  // Developer overlay (parse / derivation timings, render counts).
  // Off by default; surfaces nothing in production unless explicitly
  // toggled via Settings.
  debugOverlay: "emlx:debug-overlay",

  // Compare-mode source array (2..6 messages, JSON array of strings).
  // Replaces the legacy single-pair `compareLeft` / `compareRight` keys
  // which we still read on first load to migrate forward.
  compareSources: "emlx:compare-sources",

  // Mailbox filter queries: saved pills + a recent-query FIFO. Envelope
  // JSON shape v1: {saved: string[], recent: string[]} (see corpusQuery.ts).
  corpusQueries: "emlx:corpus-queries",

  // Local-only registry of attachment SHA-256 hashes flagged as "known
  // bad". JSON array of lowercased hex strings (64 chars each). The
  // built-in seed list lives in code; this key only stores user
  // additions. See ARCHITECTURE.md for the format.
  knownBadHashes: "emlx:known-bad-hashes",

  // Today's successful-parse counter, surfaced in the footer as "X messages
  // parsed today". JSON shape: {date: "YYYY-MM-DD", count: n}.
  parseDailyCount: "emlx:parse-daily-count",

  // Raw-vs-decoded split in the headers table. Reserved: the surface that
  // read it went with the pre-rebuild UI and the current headers section has
  // no such toggle. Kept so the name is not reused for something else.
  headersDualColumn: "emlx:headers-dual-column",

  // "Hide the route, the raw source and the indicators when printing" toggle.
  // The attribute half is live: `ExportActions` sets `data-print-redact` on
  // `<body>` and the `@media print` block in `src/next/next.css` hides those
  // sections. This KEY is not: the toggle is component state and resets with
  // the report, deliberately, so a redacted print is a choice the operator
  // makes for the export in front of them rather than a setting they may have
  // left on months ago.
  printRedact: "emlx:print-redact",

  // Attachments-tab keyboard-hint dismissal. Migrated in iter328 from
  // the legacy `mime-inspector.attachments.kbd-hint.dismissed` key.
  attachmentsHintDismissed: "emlx:attachments.kbd-hint.dismissed",

  // Verification mode the parser ran in. One of "parse-only" | "dns-enabled"
  // | "crypto-enabled" | "sandboxed". Default `parse-only` and the only
  // fully-functional mode in this batch; the DNS / Crypto / Sandbox workers
  // land in P3 / P4 / P5.
  verificationMode: "emlx:verification-mode",

  // User-edited ParserConfig (resource limits + feature toggles).
  // JSON-serialised partial of `ParserConfig`; missing keys fall back to
  // `DEFAULT_PARSER_CONFIG` from `src/perf/budgets.ts`.
  parserConfig: "emlx:parser-config",

  // Per-finding notes keyed by `${bookmarkId}::${findingId}`. Survives across
  // bookmark reopens.
  findingNotes: "emlx:finding-notes",

  // Saved Investigations: bundle of (corpus filter + saved view + finding
  // notes + cover summary).
  investigations: "emlx:investigations",

  // Per-case redaction salts. JSON map of `caseId -> { salt: hex, createdAt
  // }`. The salt NEVER leaves localStorage: every export pipeline (Y4
  // evidence-bundle.zip, exportJson) MUST omit this key. Rotating a case's
  // salt invalidates round-trips of any prior tokens for that case — used as
  // the emergency rotation lever.
  caseSalts: "emlx:case-salts",

  // Active role layout. One of the five RoleId values, or absent → universal
  // layout (today's behavior unchanged). Picking a layout reorders + hides
  // inspector tabs and seeds the suggested default tab + density. Reversible:
  // clearing the key reverts every projection.
  layoutRole: "emlx:layout:role",

  // Explicit-override flag for the density auto-follow-role behavior. When
  // set, picking a role does NOT auto-apply that role's preferred density —
  // the user's pick wins. Cleared by an explicit "follow role" toggle in
  // Settings.
  densityExplicitOverride: "emlx:density-explicit-override",

  // Opt-in left module-tree pane. Off by default. The pane shows MODULES +
  // MIME PARTS only; it never includes a source-bytes textarea or paste
  // affordance — see the risk register for Batches 36–37. Toggled exclusively
  // via Settings → Layout → "Show module navigation tree".
  showModuleTree: "emlx:layout:show-module-tree",

  // Per-group "open" flag for the secondary disclosure groups on the
  // workbench rail (`emlx:rail-group-open-{groupKey}` → "true" / "false").
  // Persisted independently per group so an analyst who pops "Trust" open can
  // leave it open across reloads while the remaining six groups stay
  // collapsed by default. The key prefix is exported via
  // `railGroupOpenKey(groupKey)`.
  railGroupOpenPrefix: "emlx:rail-group-open-",

  // First-visit guided tour. `tourCompleted` is "true" once the user finishes
  // / skips the 5-stop walkthrough; `tourVersion` is bumped alongside so a
  // future redesigned tour can re-trigger the first-visit display for
  // everyone. The OnboardingTour component reads both via the helpers in
  // `views/onboarding/tour-steps.ts`.
  tourCompleted: "emlx:tour-completed",
  tourVersion: "emlx:tour-version",

  // iOS "add to home screen" install hint dismissal. iOS Safari never fires
  // beforeinstallprompt, so PwaPrompts shows its own Share-sheet instructions
  // there; set once the visitor dismisses it so it never reappears.
  iosInstallHintDismissed: "emlx:ios-install-hint-dismissed",
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

/** Symbolic name of a registered key — typed lookup target for {@link storageKey}. */
export type StorageKeyName = keyof typeof STORAGE_KEYS;

/**
 * Typed `storageKey()` helper.
 *
 * Looks up a registered localStorage key by its symbolic name. The return
 * type narrows to the literal string registered in {@link STORAGE_KEYS}, so a
 * typo on the call site fails at compile time instead of silently missing in
 * production.
 *
 * Prefer this helper over direct property access (`STORAGE_KEYS.foo`) when
 * the consumer wants the key as a value (e.g. a generic Storage adapter);
 * both are equivalent at runtime but `storageKey("foo")` reads as the intent
 * of the call ("give me the canonical key for `foo`") rather than as an
 * object dereference.
 *
 * ARCHITECTURE.md called for a typed `storageKey()` helper since iter183.
 * Shipped in Wave 16E.
 */
export function storageKey<N extends StorageKeyName>(name: N): (typeof STORAGE_KEYS)[N] {
  return STORAGE_KEYS[name];
}

/** Prefix for all collapse-state Panel keys on the Overview tab. */
export const OVERVIEW_PANEL_PREFIX = "emlx:overview-panel:";

/** Prefix for all collapse-state Panel keys on the Security tab. */
export const SECURITY_PANEL_PREFIX = "emlx:security-panel:";

/** Build a Panel storageKey for an Overview sub-panel by suffix. */
export function overviewPanelKey(suffix: string): string {
  return `${OVERVIEW_PANEL_PREFIX}${suffix}`;
}

/** Build a Panel storageKey for a Security sub-panel by suffix. */
export function securityPanelKey(suffix: string): string {
  return `${SECURITY_PANEL_PREFIX}${suffix}`;
}

/**
 * Build the localStorage key tracking whether a secondary rail-group
 * disclosure is open. One key per group so an analyst can pop one group open
 * without ballooning a shared JSON blob each time the React state writes
 * through. Default-missing → group is closed.
 */
export function railGroupOpenKey(groupKey: string): string {
  return `${STORAGE_KEYS.railGroupOpenPrefix}${groupKey}`;
}

/**
 * Returns true for any key the app considers "ours" — used by Reset to
 * defaults.
 *
 * The legacy `mime-inspector.*` namespace (dot separator) is still in use by
 * a handful of hint-dismissal flags; the PWA banners and the iOS install hint
 * also use the `mime-inspector:*` shape (colon separator). Both shapes are
 * owned — we recognise either spelling so the reset action wipes them all.
 */
export function isOwnedStorageKey(key: string): boolean {
  return (
    key.startsWith("emlx:") ||
    key.startsWith("mime-inspector.") ||
    key.startsWith("mime-inspector:")
  );
}

/**
 * The Cache Storage names this origin owns. A third store, alongside the two
 * Web Storage areas above.
 *
 * It is registered here for the reason `STORAGE_KEYS` exists at all: the two
 * halves of Cache Storage are written and read by different files that cannot
 * import from each other. `public/sw.js` is a classic worker script copied
 * verbatim into `dist/`, so it declares its own literals; `launchIntake.ts`
 * and `SettingsView.tsx` read and delete under the same names from the page
 * side. Before this registry the share key was two independent string
 * literals, and changing the page-side one by a single character left the
 * whole suite and every gate green while the Web Share Target intake found
 * nothing and "Erase everything stored by this site" could no longer reach a
 * parked payload (filed 2026-08-28).
 *
 * The page side imports these values. `public/sw.js` cannot, so
 * `scripts/audit-storage-keys.mjs` holds its literals to this table instead,
 * in both directions.
 */
export const CACHE_STORAGE = {
  /**
   * Every cache this app's service worker opens starts with this. It is also
   * the filter `activate` uses before deleting a cache, which is what stops an
   * upgrade clobbering an unrelated app's caches on a shared dev origin.
   */
  prefix: "mime-smith-",
  /**
   * Reserved key holding one pending Web Share Target payload: the raw bytes
   * of a file the OS share sheet handed to this app. Written by `sw.js`'s
   * `handleShareTarget`, consumed by `readSharedTarget()`, swept by the erase
   * control. Never served over a GET route.
   */
  shareTarget: "/__share-target__",
  /** Reserved key holding the precached offline shell. */
  offlineShell: "/__offline__",
} as const;

/** Every reserved Cache Storage key this app owns, for the audit and tests. */
export const RESERVED_CACHE_KEYS: readonly string[] = [
  CACHE_STORAGE.shareTarget,
  CACHE_STORAGE.offlineShell,
];
